import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang, route, servicePath, counterpartPath } from "../i18n";
import { useSiteServices } from "./SiteContext";
import { Logo } from "./Logo";
import { AviahWatermark } from "./AviahMark";
import { track } from "../lib/track";

const NAV_KEYS = ["home", "about", "services", "resources", "blog", "contact"];

const Header = () => {
  const { lang, t } = useLang();
  const services = useSiteServices();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const isActive = (key) => {
    const p = route(lang, key);
    if (key === "home") return pathname === p;
    return pathname === p || pathname.startsWith(p + "/");
  };

  const alt = counterpartPath(pathname);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
          scrolled
            ? "border-b border-[#171717]/15 bg-[#F4F0E8]/95 shadow-[0_1px_0_rgba(23,23,23,0.04)] backdrop-blur-xl"
            : "border-b border-[#171717]/8 bg-[#F4F0E8]/90 backdrop-blur-xl"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
          <Logo variant="black" className="h-6 w-auto sm:h-7" testid="header-logo" />

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_KEYS.map((key) =>
              key === "services" ? (
                <div key={key} className="group relative">
                  <Link
                    to={route(lang, "services")}
                    data-testid="nav-link-services"
                    className={`relative flex items-center gap-1.5 py-2 text-[0.72rem] uppercase tracking-[0.18em] transition-colors ${
                      isActive(key) ? "text-[#8a6f3e]" : "text-[#171717]/75 hover:text-[#171717]"
                    }`}
                  >
                    {t.nav.services}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-[#C6A46A] transition-all duration-300 ${
                        isActive(key) ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </Link>
                  <div className="invisible absolute left-1/2 top-full w-72 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                    <div className="border border-[#171717]/15 bg-[#F4F0E8] p-2 shadow-xl">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={servicePath(lang, s.slug)}
                          data-testid={`nav-service-${s.slug}`}
                          className="flex items-baseline gap-3 px-4 py-3 text-sm text-[#171717]/75 transition-colors hover:bg-[#EEE9DF] hover:text-[#8a6f3e]"
                        >
                          <span className="text-[0.65rem] text-[#C6A46A]">{s.num}</span>
                          {s[lang].title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={key}
                  to={route(lang, key)}
                  data-testid={`nav-link-${key}`}
                  className={`group relative py-2 text-[0.72rem] uppercase tracking-[0.18em] transition-colors ${
                    isActive(key) ? "text-[#8a6f3e]" : "text-[#171717]/75 hover:text-[#171717]"
                  }`}
                >
                  {t.nav[key]}
                  <span
                    className={`absolute -bottom-0.5 left-0 h-px bg-[#C6A46A] transition-all duration-300 ${
                      isActive(key) ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              ),
            )}
          </nav>

          <div className="hidden items-center gap-6 lg:flex">
            <div className="flex items-center gap-2 text-[0.7rem] tracking-[0.18em]" data-testid="lang-switcher">
              <Link
                to={lang === "en" ? pathname : alt}
                data-testid="lang-switch-en"
                onClick={() => track("language_switch", { to: "en" })}
                className={lang === "en" ? "text-[#8a6f3e]" : "text-[#171717]/45 hover:text-[#171717]"}
              >
                EN
              </Link>
              <span className="text-[#171717]/25">/</span>
              <Link
                to={lang === "es" ? pathname : alt}
                data-testid="lang-switch-es"
                onClick={() => track("language_switch", { to: "es" })}
                className={lang === "es" ? "text-[#8a6f3e]" : "text-[#171717]/45 hover:text-[#171717]"}
              >
                ES
              </Link>
            </div>
            <Link
              to={route(lang, "contact")}
              data-testid="header-cta"
              onClick={() => track("book_consultation_click", { location: "header" })}
              className="border border-[#C6A46A] bg-[#C6A46A] px-5 py-2.5 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-[#171717] transition-colors duration-300 hover:bg-transparent hover:text-[#C6A46A]"
            >
              {t.nav.book}
            </Link>
          </div>

          <button
            className="flex min-h-[44px] items-center gap-2 text-[#171717] lg:hidden"
            onClick={() => setOpen(true)}
            aria-label={t.nav.menu}
            data-testid="mobile-menu-button"
          >
            <span className="text-[0.7rem] uppercase tracking-[0.18em]">{t.nav.menu}</span>
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 bg-[#F4F0E8]"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
          >
            <AviahWatermark className="absolute -right-16 bottom-0 h-72 w-72 opacity-[0.06]" />
            <div className="flex h-16 items-center justify-between px-5">
              <Logo variant="black" className="h-6 w-auto" testid="mobile-logo" />
              <button
                onClick={() => setOpen(false)}
                aria-label={t.nav.close}
                data-testid="mobile-menu-close"
                className="flex items-center gap-2 text-[#171717]"
              >
                <span className="text-[0.7rem] uppercase tracking-[0.18em]">{t.nav.close}</span>
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-6 flex flex-col px-6" aria-label="Mobile">
              {[...NAV_KEYS, "faq"].map((key, i) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.45 }}
                >
                  <Link
                    to={route(lang, key)}
                    data-testid={`mobile-nav-${key}`}
                    className={`flex items-baseline gap-4 border-b border-[#171717]/10 py-4 ${
                      isActive(key) ? "text-[#8a6f3e]" : "text-[#171717]"
                    }`}
                  >
                    <span className="text-[0.6rem] text-[#C6A46A]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-2xl font-light tracking-tight">{t.nav[key]}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-8 flex items-center gap-6 px-6"
            >
              <Link
                to={route(lang, "contact")}
                data-testid="mobile-cta"
                className="border border-[#C6A46A] bg-[#C6A46A] px-6 py-3 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-[#171717]"
              >
                {t.nav.book}
              </Link>
              <div className="flex items-center gap-2 text-[0.72rem] tracking-[0.18em]">
                <Link to={lang === "en" ? pathname : alt} data-testid="mobile-lang-en" className={lang === "en" ? "text-[#8a6f3e]" : "text-[#171717]/45"}>
                  EN
                </Link>
                <span className="text-[#171717]/25">/</span>
                <Link to={lang === "es" ? pathname : alt} data-testid="mobile-lang-es" className={lang === "es" ? "text-[#8a6f3e]" : "text-[#171717]/45"}>
                  ES
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
